import { Route, Routes } from 'react-router-dom'
import { NotFoundScreen } from './components/NotFoundScreen'
import { SiteShell } from './components/SiteShell'
import { ENGLISH, LOADERS } from './i18n'
import { LocaleProvider, postSeo } from '@kkf/design'
import { POSTS } from './data/blog'
import { ROUTES, SITE } from './data/seo'
import { AboutPage } from './pages/AboutPage'
import { BlogDetailsPage } from './pages/BlogDetailsPage'
import { BlogPage } from './pages/BlogPage'
import { ContactPage } from './pages/ContactPage'
import { EventDetailsPage } from './pages/EventDetailsPage'
import { EventsPage } from './pages/EventsPage'
import { DonationDetailsPage } from './pages/DonationDetailsPage'
import { DonationsPage } from './pages/DonationsPage'
import { ProjectDetailsPage } from './pages/ProjectDetailsPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { FaqPage } from './pages/FaqPage'
import { HomePage } from './pages/HomePage'
import { PricingPage } from './pages/PricingPage'
import { ServiceDetailsPage } from './pages/ServiceDetailsPage'
import { ServicesPage } from './pages/ServicesPage'
import { TeamDetailsPage } from './pages/TeamDetailsPage'
import { TeamPage } from './pages/TeamPage'
import { PrivacyPage, TermsPage } from './pages/LegalPages'

/**
 * Route table — 18 routes.
 *
 * The resort, the library, the garage and the Partners used to live at `/resort*`. They
 * are their own apps on their own subdomains now, and the nav, footer, services listing
 * and projects section link out to them — see `data/links.ts`. Nothing redirects the old
 * paths, deliberately: the site is pre-launch and those URLs were never published.
 *
 * `/projects/:slug` sits in its own layout group because it is the one page whose
 * `<main>` needs a class; see `AppShell`.
 */
/**
 * The site tree the shell applies on every navigation, and the same list the build writes
 * into per-route HTML — see `data/seo.ts` and `vite.config.ts`.
 *
 * Module-level on purpose: `AppShell` memoises its lookup index on this object's identity,
 * so an object literal in the render body would rebuild it on every render.
 */
const SEO = { site: SITE, routes: [...ROUTES, ...POSTS.map(postSeo)] }

/**
 * `LocaleProvider` is here rather than in `main.tsx` for the reason it is in the other
 * four apps: the route suite renders `<App/>` directly, and a provider outside it would
 * leave every test throwing from `useLocale()`.
 *
 * The shell and the 404 are components now rather than elements composed here, because
 * both need the language and `App` is the thing that provides it.
 */
export function App() {
  return (
    <LocaleProvider english={ENGLISH} loaders={LOADERS}>
    <Routes>
      <Route element={<SiteShell seo={SEO} />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:slug" element={<ServiceDetailsPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/team/:slug" element={<TeamDetailsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/donations" element={<DonationsPage />} />
        <Route path="/donations/:slug" element={<DonationDetailsPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:slug" element={<EventDetailsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogDetailsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* The two shared legal pages. One component, one source of words — see
            `@kkf/design`'s `config/legal.ts`. */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Route>

      {/* the one page whose <main> carries a class */}
      <Route element={<SiteShell seo={SEO} mainClassName="overflow-hidden" />}>
        <Route path="/projects/:slug" element={<ProjectDetailsPage />} />
      </Route>

      {/* Outside every layout group on purpose: the not-found page carries no header
          and no footer, so it must not be wrapped in `AppShell`. It provides its own
          way back instead. */}
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
    </LocaleProvider>
  )
}
